import { footprintsOverlap, tileFootprint, type FootprintTile } from '~/composables/grid/footprint'
import { getParentShape, getShapeById } from '~/composables/shapes/utils'
import { isRotationLocked } from '~/composables/shapes/shapeCapabilities'
import { SHAPES as HEX_SPACED_SHAPES } from '~/composables/shapes/shapesForHexSpaced'

export type SelectionTile = {
  id: string
  x: number
  y: number
  shapeId: string
  rotation: number
  width: number
  height: number
}

export type TileMove = {
  id: string
  x: number
  y: number
  nextShapeId: string | null
  nextRotation: number
}

export type RotateSelectionResult = {
  anchor: { x: number; y: number }
  moves: TileMove[]
  shiftedToFit: boolean
  collidingIds: string[]
}

// 'same' looks identical after a quarter turn. 'fixed' has no turned variant and keeps its orientation.
// Both only move their center.
export type ClockwiseTarget = { id: string; rotation: number } | 'same' | 'fixed'

// 90 for axis aligned grids. 60 for hex grids, the only turn that maps a hex lattice onto itself.
export type TurnAngle = 90 | 60

export type RotateOptions = {
  resolve?: (shapeId: string, rotation: number, angle: TurnAngle) => ClockwiseTarget
  pivot?: { x: number; y: number }
  angle?: TurnAngle
  // Lattice step on y when it differs from the x step (hex grids).
  snapY?: number
  // Where the grid's lattice starts. The canvas lattice is offset from 0 by its start margin.
  origin?: number
  // The grid's own placement snap, for grids where a turn does not land exactly on the lattice (45 degrees, hex).
  snapPoint?: (point: { x: number; y: number }, tile: SelectionTile, next: { shapeId: string; rotation: number }) => { x: number; y: number }
  // Off on grids where the app never checks overlaps (45 degrees and the hex grids).
  detectOverlap?: boolean
}

const CANVAS_MARGIN = 20

// Store items keep their shape id and position either on the item or under attrs.
type StoredPlacement = { x?: number; y?: number; rotation?: number; width?: number; height?: number; metadata?: { shape_id?: string } }
type StoreItem = StoredPlacement & { attrs?: StoredPlacement }

const itemFootprintInput = (item: StoreItem): FootprintTile => ({
  shapeId: item.metadata?.shape_id ?? item.attrs?.metadata?.shape_id,
  x: item.x ?? item.attrs?.x ?? 0,
  y: item.y ?? item.attrs?.y ?? 0,
  rotation: item.rotation ?? item.attrs?.rotation ?? 0,
  width: item.width ?? item.attrs?.width,
  height: item.height ?? item.attrs?.height,
})

// Spaced Hex lists its diamond turns as separate shapes instead of children.
const spacedDiamonds = HEX_SPACED_SHAPES.filter(s => s.type === 'diamond')

export const clockwiseTarget = (shapeId: string, rotation = 0, angle: TurnAngle = 90): ClockwiseTarget => {
  const shape = getShapeById(shapeId)
  if (!shape) return 'fixed'
  // Locked shapes (wood slats) have no variant ids. Block rotation turns them by item rotation only.
  if (isRotationLocked(shape)) return { id: shapeId, rotation: (rotation + 90) % 360 }

  // A hexagon looks the same after a 60 degree turn, even where the freeform list pairs Hex with Hex90.
  if (angle === 60 && shape.type === 'hex') return 'same'

  const root = getParentShape(shapeId) ?? shape
  const family = root.children?.length
    ? [root, ...root.children]
    : spacedDiamonds.some(s => s.id === shapeId) ? spacedDiamonds : []

  if (family.length === 0) {
    // A square rect and a full circle are the only no-family shapes that look the same after 90 degrees.
    const fullCircle = shape.type === 'arc' && (shape.angle ?? 360) === 360
    const square = shape.type === 'rect' && shape.w === shape.h
    return square || fullCircle ? 'same' : 'fixed'
  }

  const rotationOf = (s: { rotation?: number }) => s.rotation || 0
  const current = rotationOf(family.find(s => s.id === shapeId) ?? shape)
  const turnable = (id: string) => ({ id, rotation: rotationOf(family.find(s => s.id === id)!) })

  if (angle === 60) {
    // Hex families list their variants 60 degrees apart, so the next one clockwise follows in rotation order.
    const ordered = [...family].sort((a, b) => ((rotationOf(a) - rotationOf(root) + 360) % 360) - ((rotationOf(b) - rotationOf(root) + 360) % 360))
    const next = ordered[(ordered.findIndex(s => s.id === shapeId) + 1) % ordered.length]
    return turnable(next.id)
  }
  if (family.length === 4 && new Set(family.map(rotationOf)).size === 4) {
    const target = family.find(s => rotationOf(s) === (current + 90) % 360)
    return target ? turnable(target.id) : 'fixed'
  }
  // Two rect variants differ by a quarter turn. Two-variant arcs (half circles) face a different way, so a swap would flip them.
  if (family.length === 2 && shape.type === 'rect') {
    return turnable(family.find(s => s.id !== shapeId)!.id)
  }
  return 'fixed'
}

// Half extents of a tile's bounding box, exact for quarter turns.
const halfExtents = (width: number, height: number, rotation: number) => {
  const rad = (rotation * Math.PI) / 180
  const c = Math.abs(Math.round(Math.cos(rad) * 1e9) / 1e9)
  const s = Math.abs(Math.round(Math.sin(rad) * 1e9) / 1e9)
  return { w: (width * c + height * s) / 2, h: (width * s + height * c) / 2 }
}

/**
 * Turns a selection clockwise as one block about its snapped bbox center: 90 degrees, or 60 on hex grids.
 * `others` are the tiles outside the selection, used only to report overlaps. Tiles that cannot turn keep their
 * orientation and only move around the anchor.
 */
export const rotateSelectionClockwise = (
  tiles: SelectionTile[],
  snap: number,
  others: Array<{ id: string }>,
  { resolve = clockwiseTarget, pivot, angle = 90, snapY = snap, origin = 0, snapPoint, detectOverlap = true }: RotateOptions = {},
): RotateSelectionResult => {
  const targets = tiles.map(tile => resolve(tile.shapeId, tile.rotation, angle))

  const halves = tiles.map(tile => halfExtents(tile.width, tile.height, tile.rotation))
  const minX = Math.min(...tiles.map((t, i) => t.x - halves[i].w))
  const maxX = Math.max(...tiles.map((t, i) => t.x + halves[i].w))
  const minY = Math.min(...tiles.map((t, i) => t.y - halves[i].h))
  const maxY = Math.max(...tiles.map((t, i) => t.y + halves[i].h))
  const rad = (angle * Math.PI) / 180
  const cos = angle === 90 ? 0 : Math.cos(rad)
  const sin = angle === 90 ? 1 : Math.sin(rad)
  const turn = (a: { x: number; y: number }, tile: { x: number; y: number }) => ({
    x: a.x + (tile.x - a.x) * cos - (tile.y - a.y) * sin,
    y: a.y + (tile.x - a.x) * sin + (tile.y - a.y) * cos,
  })
  const snapped = (p: { x: number; y: number }, i: number) => {
    const target = targets[i]
    const keeps = typeof target === 'string'
    return snapPoint ? snapPoint(p, tiles[i], { shapeId: keeps ? tiles[i].shapeId : target.id, rotation: keeps ? tiles[i].rotation : target.rotation }) : p
  }
  const centerX = origin + Math.round(((minX + maxX) / 2 - origin) / snap) * snap
  const centerY = origin + Math.round(((minY + maxY) / 2 - origin) / snapY) * snapY

  // A half-step bbox center rounds differently after each turn, so repeat turns pass the previous pivot back in.
  let anchor = pivot ?? { x: centerX, y: centerY }
  if (!pivot && snapPoint) {
    // Only some pivots map the grid's lattice onto itself, so take the nearby one that leaves tiles least off it.
    const offLattice = (a: { x: number; y: number }) => tiles.reduce((sum, tile, i) => {
      const exact = turn(a, tile)
      const rounded = snapped(exact, i)
      return sum + Math.hypot(rounded.x - exact.x, rounded.y - exact.y)
    }, 0)
    let best = { score: Infinity, dist: Infinity }
    for (let dx = -2; dx <= 2; dx++) {
      for (let dy = -2; dy <= 2; dy++) {
        const candidate = { x: centerX + dx * snap, y: centerY + dy * snapY }
        const score = offLattice(candidate)
        const dist = Math.hypot(dx * snap, dy * snapY)
        if (score < best.score - 1e-6 || (Math.abs(score - best.score) <= 1e-6 && dist < best.dist)) {
          best = { score, dist }
          anchor = candidate
        }
      }
    }
  }

  const moves: TileMove[] = tiles.map((tile, i) => {
    const target = targets[i]
    const keeps = typeof target === 'string'
    return {
      id: tile.id,
      ...snapped(turn(anchor, tile), i),
      nextShapeId: keeps ? null : target.id,
      nextRotation: keeps ? tile.rotation : target.rotation,
    }
  })

  // A quarter turned tile swaps its half extents. A fixed tile keeps them. Other angles use the larger half.
  const newHalf = (i: number, axis: 'x' | 'y') => {
    if (angle !== 90) return Math.max(halves[i].w, halves[i].h)
    const swap = targets[i] !== 'fixed'
    return (axis === 'x') === swap ? halves[i].h : halves[i].w
  }
  const overshoot = (axis: 'x' | 'y') => Math.max(
    0,
    ...moves.map((m, i) => CANVAS_MARGIN - (m[axis] - newHalf(i, axis))),
  )
  const shift = {
    x: Math.ceil(overshoot('x') / snap) * snap,
    y: Math.ceil(overshoot('y') / snapY) * snapY,
  }
  for (const m of moves) {
    m.x += shift.x
    m.y += shift.y
  }

  // Selected tiles are checked against each other too, since a fixed tile can overlap a turned one.
  const selectedIds = new Set(tiles.map(tile => tile.id))
  const footprints = [
    ...moves.map((m, i) => ({ id: m.id, shape: tileFootprint({ ...tiles[i], shapeId: m.nextShapeId ?? tiles[i].shapeId, x: m.x, y: m.y, rotation: m.nextRotation }) })),
    ...others.filter(shape => !selectedIds.has(shape.id)).map(shape => ({ id: shape.id, shape: tileFootprint(itemFootprintInput(shape as StoreItem)) })),
  ]
  const collidingIds = !detectOverlap ? [] : moves
    .map(m => m.id)
    .filter((_, i) => footprints.some((other, j) => j !== i && footprintsOverlap(footprints[i].shape, other.shape)))

  return { anchor: { x: anchor.x + shift.x, y: anchor.y + shift.y }, moves, shiftedToFit: shift.x > 0 || shift.y > 0, collidingIds }
}
